import { Fragment, memo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { ColumnPinningState, ColumnSizingState, Table, VisibilityState } from '@tanstack/react-table';
import type { SvgIconComponent } from '@mui/icons-material';
import { KeyboardArrowDown, PushPin } from '@mui/icons-material';
import { Box, TableCell, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { StickyTableHead, StyledTableCell, TableCellStyledBody } from './TableHeader.styles';
import { IconWithDropdown } from 'components/common/Inputs';
import { useTranslation } from 'react-i18next';
import type { DataSchema, Schema } from 'types';
import { Checkbox } from 'components/mui/common/Checkbox';
import { BUTTONS_GROUP_ATTRIBUTE_KEY } from 'utils/agendaRecord/record.ts';
import { getSortHandler, getTableControlsValues, isColumnSortable } from 'utils/table/user/tableHeader.ts';
import { getCommonPinningStyles } from './tableFunctions';
import { useTableManagement } from 'contexts/TableManagementContext.tsx';
import type { TableFormData } from './Table.tsx';
import { ResizerButton } from './actions/ResizerButton.tsx';
import { PinButton } from './actions';
import type { TableColumnAggregationState } from 'utils/table.ts';
import type { ColumnAggregationType } from 'types/globalStore.ts';

import { FeatureGate } from '../Posthog/FeatureGate.tsx';
import { useFeatureFlag } from 'utils/hooks/posthog/useFeatureFlag.ts';
import { mapTextAlignToCss, mapTextAlignToJustifyContent } from 'utils/styles';

interface Props<T extends keyof P, P extends DataSchema> {
  tableInstance: Table<Schema<T, P>>;
  selectAllForm: UseFormReturn<TableFormData<boolean>>;
  onSelectAll: () => void;
  unableSortFor: (keyof P)[] | 'none' | 'all';

  sort?: {
    column: keyof P;
    direction: 'asc' | 'desc';
  };
  onSort?: (sort: { column: keyof P; direction: 'asc' | 'desc' }) => void;

  hideCheckbox?: boolean;
  isMultiSelect?: boolean;
  onAddButtonClick?: () => void;
  tableControls?: {
    [key: string]: {
      visible: boolean;
      disabled?: boolean;
      onClick: () => void;
      icon: SvgIconComponent;
      label: string;
      id: string;
    };
  };
  /**
   * Rerender table header when column sizing changes
   */
  columnSizing?: ColumnSizingState;
  columnPinning?: ColumnPinningState;
  columnVisibility?: VisibilityState;
  columnAggregation?: TableColumnAggregationState;
  onColumnAggregationChange?: (columnId: string, aggregation: ColumnAggregationType | null) => void;
  onColumnSizingReset?: (columnId: string) => void;
  onManualColumnResizeStart?: (columnId: string) => void;
  isColumnSizingResetEnabled?: boolean;
  isEnableColumnAggregation?: boolean;
  dataTestIdPrefix?: string;
  testIdPrefix?: string;
}

type ColumnMeta<P extends DataSchema> = {
  accessorKey?: string;
  processedSchema?: P;
  automaticSizing?: boolean;
  columnOverflowMode?: string;
};

const TableHeaderBase = <T extends keyof P, P extends DataSchema>({
  tableInstance,
  selectAllForm,
  sort,
  onSort,
  onSelectAll,
  unableSortFor,
  tableControls,
  hideCheckbox,
  isMultiSelect = true,
  columnAggregation,
  onColumnAggregationChange,
  onColumnSizingReset,
  onManualColumnResizeStart,
  isColumnSizingResetEnabled = false,
  isEnableColumnAggregation = true,
  testIdPrefix,
  columnSizing,
  dataTestIdPrefix,
}: Props<T, P>) => {
  const resolvedTestIdPrefix = dataTestIdPrefix ?? testIdPrefix;
  const { t } = useTranslation();
  const { control: selectAllControl } = selectAllForm;
  const tableControlsValues = getTableControlsValues(tableControls);
  const enabled_F202718 = useFeatureFlag('F20-2718');
  const hasEnabled_F202718 = Boolean(enabled_F202718);

  const { hasInnerTable, shouldShowRowControls, numberOfVisibleTableControls, hasHeliosFunctions, hasEditableActions, draggable } =
    useTableManagement();

  const metaColumnsCount = Number(shouldShowRowControls) + Number(hasHeliosFunctions);

  return (
    <StickyTableHead>
      <TableRow>
        {!hideCheckbox && (
          <TableCell sx={{ padding: 0, width: 'calc(var(--col-selection-size) * 1px)' }}>
            {isMultiSelect ? (
              <Controller
                name="selectAll"
                control={selectAllControl}
                render={({ field }) => (
                  <Checkbox
                    sx={{ padding: '0 9px' }}
                    {...field}
                    onChange={onSelectAll}
                    checked={field.value}
                    data-testid={resolvedTestIdPrefix ? `${resolvedTestIdPrefix}-select-all-checkbox` : 'table-select-all-checkbox'}
                  />
                )}
              />
            ) : null}
          </TableCell>
        )}
        {hasInnerTable && <TableCell sx={{ padding: 0, width: 'calc(var(--col-meta-size) * 1px)' }} />}
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <Fragment key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const {
                id: headerId,
                column: {
                  columnDef: { header: headerName },
                },
              } = header ?? {};

              if (headerId === 'id' || headerId === 'hidden') return;
              if (!header.column.getIsVisible()) return null;

              const isSortable = isColumnSortable(headerId, unableSortFor);
              const canResize = header.column.getCanResize();
              const canPin = header.column.getCanPin();
              const isColumnWidthChanged = columnSizing?.[header.column.id] !== undefined;
              const isPinned = header.column.getIsPinned();
              const columnMeta = header.column.columnDef.meta as ColumnMeta<P> | undefined;
              const accessorKey = String(columnMeta?.accessorKey ?? header.column.id);
              const labelTextAlignValue = columnMeta?.processedSchema?.[accessorKey]?.formatting?.formatLabel?.textAlign;
              const labelTextAlign = mapTextAlignToCss(labelTextAlignValue);
              const labelJustifyContent = mapTextAlignToJustifyContent(labelTextAlignValue);

              const columnName = headerName?.toString() ?? '';
              const isActionsColumn = columnName === BUTTONS_GROUP_ATTRIBUTE_KEY;
              const headerText = isActionsColumn ? '' : columnName;

              const handleSort = getSortHandler<P>(headerId, sort, onSort, isActionsColumn);

              const isHeaderActionsColumn = headerId === 'actions' || isActionsColumn;

              const headerTypographySx = {
                display: 'block',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: labelTextAlign,
              } as const;

              let headerElement = isSortable ? (
                <TableSortLabel
                  data-testid="table-header-cell-label-sortable"
                  IconComponent={KeyboardArrowDown}
                  active={sort?.column === headerId}
                  hideSortIcon={isActionsColumn}
                  direction={sort?.direction}
                  onClick={handleSort}
                  sx={{ maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={headerTypographySx}>
                    {headerText}
                  </Typography>
                </TableSortLabel>
              ) : (
                <Typography variant="subtitle2" fontWeight={700} sx={headerTypographySx}>
                  {headerText}
                </Typography>
              );

              if (!isActionsColumn && columnName) {
                headerElement = (
                  <Tooltip title={columnName} placement="top">
                    {headerElement}
                  </Tooltip>
                );
              }

              return (
                <TableCell
                  key={headerId}
                  data-auto-column={columnMeta?.automaticSizing && headerText ? header.column.id : undefined}
                  sx={{
                    whiteSpace: 'nowrap',
                    ...getCommonPinningStyles(header.column, { type: 'header' }),
                    width: `calc(var(--header-${CSS.escape(header.id)}-size) * 1px)`,
                    '&:hover .resizer': {
                      opacity: 1,
                    },
                  }}>
                  <TableCellStyledBody
                    hasEnabled_F202718={hasEnabled_F202718}
                    sx={columnMeta?.columnOverflowMode ? { px: '8px', boxSizing: 'border-box' } : undefined}>
                    {isPinned && <PushPin sx={{ height: '16px', width: '16px', color: 'primary.light', flexShrink: 0 }} />}
                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', justifyContent: labelJustifyContent, alignItems: 'center' }}>
                      {headerElement}
                    </Box>
                    {canPin && !isHeaderActionsColumn && (
                      <FeatureGate flag="F20-2718">
                        <PinButton
                          header={header}
                          aggregation={columnAggregation?.[accessorKey] ?? null}
                          onAggregationChange={(aggregation) => onColumnAggregationChange?.(accessorKey, aggregation)}
                          onColumnSizingReset={() => onColumnSizingReset?.(header.id)}
                          isColumnWidthChanged={isColumnWidthChanged}
                          isColumnSizingResetEnabled={isColumnSizingResetEnabled}
                          isEnableColumnAggregation={isEnableColumnAggregation}
                        />
                      </FeatureGate>
                    )}
                    {canResize && !isHeaderActionsColumn && (
                      <FeatureGate flag="F20-2718">
                        <ResizerButton header={header} onResizeStart={onManualColumnResizeStart} />
                      </FeatureGate>
                    )}
                  </TableCellStyledBody>
                </TableCell>
              );
            })}
          </Fragment>
        ))}
        {metaColumnsCount > 0 && (
          <StyledTableCell
            align="right"
            sx={{
              width: `calc(var(--col-meta-size) * ${metaColumnsCount + Number(shouldShowRowControls)}px)`,
              minWidth: `calc(var(--col-meta-size) * ${metaColumnsCount + Number(shouldShowRowControls)}px)`,
              position: 'sticky',
              right: (hasEditableActions ? 100 : 0) + (draggable ? 40 : 0),
              zIndex: 3,
              backgroundColor: 'background.default',
              boxShadow: '-6px 0 8px -4px rgba(0,0,0,0.15)',
            }}>
            {shouldShowRowControls && numberOfVisibleTableControls > 0 && (
              <IconWithDropdown helperText={t('common.tableControls')} values={tableControlsValues} returnValue={null} />
            )}
          </StyledTableCell>
        )}
        {draggable && (
          <StyledTableCell
            sx={{
              width: 'calc(var(--col-meta-size) * 1px)',
              position: 'sticky',
              right: hasEditableActions ? 100 : 0,
              zIndex: 3,
              backgroundColor: 'background.default',
            }}
          />
        )}
        {hasEditableActions && (
          <TableCell
            sx={{
              padding: '8px',
              width: 'calc(var(--col-actions-size) * 1px)',
              position: 'sticky',
              right: 0,
              zIndex: 3,
              backgroundColor: 'background.default',
            }}
          />
        )}
      </TableRow>
    </StickyTableHead>
  );
};
export const TableHeader = memo(TableHeaderBase) as typeof TableHeaderBase;
